import { useState, type ReactNode } from 'react'
import { Link, useNavigate, useSearch } from '@tanstack/react-router'
import { mutate } from '@/runtime'
import { useDates, useProjects, useTasks } from '@/db/hooks'
import { useNow } from '@/hooks/useNow'
import type { TaskResource } from '@/kinds/task/apply'
import { addTaskFromText } from '@/components/QuickAdd'
import { splitLines } from '@/kinds/task/parse'
import { taskActions } from '@/lib/actions'
import { joinTitles } from '@/lib/day'
import {
  isFree,
  jobsFor,
  lookBack,
  looseEnds,
  planDays,
  planKindFor,
  planWindow,
  primeAsk,
  type PlanKind,
  type PlanWindow,
} from '@/lib/plan'
import { parseDate, todayLocal } from '@/lib/time'
import { useScope } from '@/state/scope'
import { ALL } from '@/kinds/workspace/schema'
import { Button, Field, PageTitle } from '@/components/ui'
import { AppTabs, REVIEW_TABS } from '@/components/AppTabs'
import { DateRow } from '@/components/DateBits'
import { PrimeMark } from '@/components/PrimeMark'

/** How many names a section shows before it points elsewhere (Show less). */
const SHOWN = 5

const weekday = (day: string) => parseDate(day).toLocaleDateString(undefined, { weekday: 'short' })
const dayHeading = (day: string, today: string) =>
  day === today
    ? 'Today'
    : parseDate(day).toLocaleDateString(undefined, { weekday: 'long', day: 'numeric', month: 'short' })

/** "12 – 18 October", "28 October – 3 November", or one day alone. */
const rangeOf = (w: PlanWindow) => {
  const a = parseDate(w.from)
  const b = parseDate(w.to)
  const month = (d: Date) => d.toLocaleDateString(undefined, { month: 'long' })
  if (w.from === w.to)
    return `${a.toLocaleDateString(undefined, { weekday: 'long' })} ${a.getDate()} ${month(a)}`
  const start = a.getMonth() === b.getMonth() ? `${a.getDate()}` : `${a.getDate()} ${month(a)}`
  return `${start} – ${b.getDate()} ${month(b)}`
}

const Label = ({ children }: { children: ReactNode }) => (
  <div className="text-[13px] font-semibold text-muted mb-1.5">{children}</div>
)

/**
 * A day for something, from the plan's own days: one tap schedules it and
 * takes it out of the inbox (as quick add's "When?" does), one undoable step.
 */
const PlaceChips = ({
  task,
  w,
  onPlaced,
  extra,
}: {
  task: TaskResource
  w: PlanWindow
  onPlaced?: (day: string) => void
  extra?: ReactNode
}) => {
  const give = (day: string) =>
    void mutate
      .grouped(async () => {
        await mutate.action(task.id, 'defer', { scheduled: day })
        if (task.status.state !== 'next') await mutate.action(task.id, 'move', { state: 'next' })
      })
      .then(() => onPlaced?.(day))
  return (
    <div className="flex flex-wrap items-center gap-1.5 px-3.5 pb-2.5 -mt-1" data-testid="plan-place">
      {w.days.map((d) => (
        <button
          key={d}
          type="button"
          onClick={() => give(d)}
          className="shrink-0 rounded-full bg-raised border border-line px-3 min-h-8 text-xs hover:bg-line"
        >
          {weekday(d)}
        </button>
      ))}
      {extra}
    </div>
  )
}

/**
 * The week's and the weekend's plan at /plan (?for=week|weekend), opened by
 * the Sunday and Friday pushes and from Review: what got done, loose ends,
 * each day and what is on it, and a box to empty your head into, each line a
 * task that can be given one of the plan's days there and then. Prime can be
 * asked to help shape it. Nothing undone is counted.
 */
export const PlanView = () => {
  const search = useSearch({ strict: false }) as { for?: PlanKind }
  const today = todayLocal()
  const kind: PlanKind = search.for ?? planKindFor(today)
  const tasks = useTasks()
  const projects = useProjects() ?? []
  const dates = useDates()
  const ws = useScope((s) => s.ws)
  const navigate = useNavigate()
  const now = useNow(60_000)
  const [draft, setDraft] = useState('')
  const [added, setAdded] = useState<{ id: string; title: string; placed?: string }[]>([])
  const [dumped, setDumped] = useState<string[]>([])

  const w = planWindow(kind, today)
  const all = tasks ?? []
  const days = planDays(w, all, dates)
  const loose = looseEnds(w, all, today)
  const done = lookBack(all, new Date(now))
  const jobs = kind === 'weekend' ? jobsFor(w, all) : []
  const byId = new Map(all.map((t) => [t.id, t]))
  const range = rangeOf(w)

  const add = async () => {
    const lines = splitLines(draft)
    if (!lines.length) return
    setDraft('')
    setDumped((d) => [...d, ...lines])
    const made: { id: string; title: string }[] = []
    for (const l of lines) {
      const r = await addTaskFromText(l, projects, { workspace: ws === ALL ? undefined : ws })
      if (r) made.push({ id: r.id, title: String((r.spec as { title?: unknown }).title ?? l) })
    }
    setAdded((a) => [...a, ...made])
  }
  const askPrime = () =>
    void navigate({ to: '/agents', search: { ask: primeAsk(w, range, dumped) } as never })

  return (
    <div className="fade-in" data-testid="plan">
      <PageTitle
        eyebrow={range}
        right={
          <Link
            to="/plan"
            search={{ for: kind === 'week' ? 'weekend' : 'week' } as never}
            className="text-xs text-muted hover:text-fg min-h-8 flex items-center"
            data-testid="plan-switch"
          >
            {kind === 'week' ? 'The weekend' : 'The week'}
          </Link>
        }
      >
        {kind === 'week' ? 'The week ahead' : 'The weekend'}
      </PageTitle>
      <AppTabs tabs={REVIEW_TABS} label="Review" />

      {done.length > 0 && (
        <section className="mb-5" data-testid="plan-done">
          <Label>Done in the last seven days</Label>
          <ul className="group-list">
            {done.slice(0, SHOWN).map((t) => (
              <li key={t.id} className="px-3.5 py-2.5 text-sm text-fg/90">
                {t.spec.title}
              </li>
            ))}
          </ul>
          {done.length > SHOWN && (
            <Link to="/log" className="inline-block mt-2 text-xs text-muted hover:text-fg">
              The rest is in the Log
            </Link>
          )}
        </section>
      )}

      {loose.length > 0 && (
        <section className="mb-5" data-testid="plan-loose">
          <Label>Loose ends</Label>
          <p className="text-xs text-faint -mt-1 mb-1.5">
            Their day went by. Give each a new one, or let it go.
          </p>
          <ul className="group-list">
            {loose.slice(0, SHOWN).map((t) => (
              <li key={t.id}>
                <Link to="/tasks/$id" params={{ id: t.id }} className="block px-3.5 pt-2.5 pb-2 text-[15px]">
                  {t.spec.title}
                </Link>
                <PlaceChips
                  task={t}
                  w={w}
                  extra={
                    <>
                      <button
                        type="button"
                        onClick={() => void taskActions.move(t, 'someday')}
                        className="shrink-0 rounded-full px-3 min-h-8 text-xs text-muted hover:text-fg"
                      >
                        Someday
                      </button>
                      <button
                        type="button"
                        onClick={() => void taskActions.drop(t)}
                        className="shrink-0 rounded-full px-3 min-h-8 text-xs text-muted hover:text-fg"
                      >
                        Drop
                      </button>
                    </>
                  }
                />
              </li>
            ))}
          </ul>
          {loose.length > SHOWN && (
            <Link to="/review" className="inline-block mt-2 text-xs text-muted hover:text-fg">
              The rest are in the review
            </Link>
          )}
        </section>
      )}

      <section className="mb-5" data-testid="plan-days">
        <Label>{kind === 'week' ? 'The days' : 'What’s on'}</Label>
        <div className="flex flex-col gap-3">
          {days.map((d) => (
            <div key={d.day}>
              <div className="text-xs text-faint mb-1">{dayHeading(d.day, today)}</div>
              {isFree(d) ? (
                <p className="text-sm text-faint px-0.5">Nothing yet</p>
              ) : (
                <ul className="group-list">
                  {d.fixed.map((f) => (
                    <DateRow key={f.date.id} date={f.date} on={d.day} today={today} />
                  ))}
                  {d.dates.map((x) => (
                    <DateRow key={x.id} date={x} on={d.day} today={today} />
                  ))}
                  {[...d.tasks, ...d.due].map((t) => (
                    <li key={t.id}>
                      <Link
                        to="/tasks/$id"
                        params={{ id: t.id }}
                        className="flex items-baseline justify-between gap-3 px-3.5 py-2.5 min-h-11 text-[15px]"
                      >
                        <span className="min-w-0">{t.spec.title}</span>
                        <span className="text-xs text-faint shrink-0">
                          {t.spec.scheduled === d.day ? (t.spec.scheduled_time ?? '') : 'Due'}
                        </span>
                      </Link>
                    </li>
                  ))}
                </ul>
              )}
            </div>
          ))}
        </div>
      </section>

      {jobs.length > 0 && (
        <section className="mb-5" data-testid="plan-jobs">
          <Label>Jobs that want doing</Label>
          <ul className="group-list">
            {jobs.slice(0, SHOWN).map((t) => (
              <li key={t.id}>
                <Link to="/tasks/$id" params={{ id: t.id }} className="block px-3.5 pt-2.5 pb-2 text-[15px]">
                  {t.spec.title}
                </Link>
                <PlaceChips task={t} w={w} />
              </li>
            ))}
          </ul>
        </section>
      )}

      <section className="mb-5">
        <Field
          label={kind === 'week' ? 'What’s on your mind for the week?' : 'What do you want from the weekend?'}
        >
          <textarea
            value={draft}
            onChange={(e) => setDraft(e.target.value)}
            rows={5}
            placeholder={
              kind === 'week'
                ? 'ring the dentist\nsend the invoice >tue\nbook the car in'
                : 'long walk\nfix the bike\nfilm night with Keira'
            }
            className="w-full rounded-2xl"
            data-testid="plan-dump"
          />
        </Field>
        <div className="flex items-start justify-between gap-3 -mt-1">
          <span className="text-xs text-faint pt-1">
            {added.length
              ? `Added ${added.length <= 3 ? joinTitles(added.map((a) => a.title)) : 'those'}. Give each a day below.`
              : 'One per line, into the inbox; >tue, +project and @someone work too.'}
          </span>
          <Button
            variant="primary"
            onClick={() => void add()}
            onPointerDown={(e) => e.preventDefault()}
            disabled={!draft.trim()}
            data-testid="plan-add"
          >
            Add
          </Button>
        </div>
        {added.length > 0 && (
          <ul className="group-list mt-3" data-testid="plan-added">
            {added.map((a) => {
              const t = byId.get(a.id)
              return (
                <li key={a.id}>
                  <div className="flex items-baseline justify-between gap-3 px-3.5 pt-2.5 pb-2 text-[15px]">
                    <span className="min-w-0">{a.title}</span>
                    {a.placed && <span className="text-xs text-muted shrink-0">{weekday(a.placed)}</span>}
                  </div>
                  {t && !a.placed && (
                    <PlaceChips
                      task={t}
                      w={w}
                      onPlaced={(day) =>
                        setAdded((xs) => xs.map((x) => (x.id === a.id ? { ...x, placed: day } : x)))
                      }
                    />
                  )}
                </li>
              )
            })}
          </ul>
        )}
      </section>

      <section className="card px-3.5 py-3 mb-5 flex items-center justify-between gap-3">
        <div className="text-sm text-muted">
          {dumped.length ? 'Want a hand putting it in order?' : 'Rather talk it through?'}
        </div>
        <Button className="shrink-0 whitespace-nowrap" onClick={askPrime} data-testid="plan-ask">
          <PrimeMark size={16} aria-hidden />
          Ask Prime
        </Button>
      </section>

      <p className="flex gap-4">
        <Link to="/" className="text-xs text-faint hover:text-fg">
          Today
        </Link>
        <Link to="/upcoming" className="text-xs text-faint hover:text-fg">
          Calendar
        </Link>
      </p>
    </div>
  )
}
